/**
 * orb-docking — pure "has the orb docked yet" derivation for CallScreen's
 * Turn 17 choreography (design refs: t17-head-and-screen.html's 17a/17b/17c,
 * t17cd.html's 17d).
 *
 * Owner-locked decisions this encodes (17c):
 *   1. Dock on the FIRST CALL CONTENT, not on connect — `hasCallContent`
 *      shares transcript-rail.tsx's own call-window selection
 *      (call-transcript.ts) rather than re-deriving it, so the two can never
 *      disagree about what counts as "the first message is ready to render".
 *   2. Stay docked for the rest of the call, never re-center —
 *      `computeDocked` is a LATCH: once true, it stays true regardless of
 *      what the inputs do afterward (a post-dock pinned-card dismissal or a
 *      mid-call failure must not un-dock). CallScreen holds the actual latch
 *      in a `useState`; this function is the pure "given the previous latch
 *      value and this render's inputs, what's the next value" step, kept
 *      free of React so it's trivially unit-testable. The latch resets for
 *      free on remount — page.tsx gates the whole CallScreen overlay on
 *      `callPhase !== 'idle'` (see call-phase.ts), so every fresh call
 *      attempt is a fresh CallScreen instance with fresh state.
 *   3. Docking never triggers during `connecting`/`failed` — only `live`.
 */

import type { Message } from '@/types/chat';
import type { PinnedCard } from './pin-routing';
import type { CallPhase } from './call-phase';
import { selectCallWindowMessages } from './call-transcript';

/**
 * True once there's something for the docked transcript to show: any
 * finalized/streaming message in the call window (selectCallWindowMessages
 * already includes in-flight streaming turns), OR an active pinned card.
 * Transport-agnostic — reads only `messages`/`pinnedCard`, so it works the
 * same for the `?rtc=0` (ws) path as the default rtc transport.
 */
export function hasCallContent(
  messages: Message[],
  callStartIndex: number,
  pinnedCard: PinnedCard | null
): boolean {
  return (
    pinnedCard !== null ||
    selectCallWindowMessages(messages, callStartIndex).length > 0
  );
}

/**
 * The dock latch's pure transition: stays `true` forever once it's been
 * `true` (decision 2 above); otherwise flips to `true` only when the phase
 * is genuinely `live` AND there's content ready (decisions 1 + 3). Every
 * other phase/content combination is a no-op (still docked, or not yet).
 */
export function computeDocked(
  prevDocked: boolean,
  callPhase: CallPhase,
  contentReady: boolean
): boolean {
  return prevDocked || (callPhase === 'live' && contentReady);
}
